import React, { useEffect, useState, useRef } from 'react';
import './MyProfilePage.css';
import { getAuth } from 'firebase/auth';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  orderBy,
  getDocs,
} from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import CameraIcon from '../assets/icons/CameraIcon.png';
import CropModal from '../components/CropModal';
import PopupModal from '../components/PopupModal';
import { playSound } from '../utils/soundPlayer';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { updatePseudoInTournaments } from '../utils/profileUtils';
import ProfileRewards from '../components/ProfileRewards';

const MyProfilePage = () => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [userData, setUserData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [showBannerCrop, setShowBannerCrop] = useState(false);
  const [rawBanner, setRawBanner] = useState(null);
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [popupMessage, setPopupMessage] = useState('');
  const [currentTournaments, setCurrentTournaments] = useState([]);
  const [showQuestModal, setShowQuestModal] = useState(false);
  const [globalQuests, setGlobalQuests] = useState([]);
  const [userQuestProgress, setUserQuestProgress] = useState({});
  const [masterDuelId, setMasterDuelId] = useState('');
  const [editingMasterDuelId, setEditingMasterDuelId] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [editingPseudo, setEditingPseudo] = useState(false);
  const [newPseudo, setNewPseudo] = useState('');

  const handleSavePseudo = async () => {
    const user = getAuth().currentUser;
    if (!user) return;
    setSaving(true);
    try {
      // Use backup wrapper for pseudo update
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, {
        pseudo: newPseudo
      }, 'profile_update', {
        field: 'pseudo',
        previousValue: userData.pseudo,
        newValue: newPseudo
      });
      
      // Update pseudo in all tournaments where user is a participant
      try {
        const result = await updatePseudoInTournaments(newPseudo);
        console.log(`✅ Pseudo updated in ${result.tournamentsUpdated} tournaments`);
      } catch (tournamentError) {
        console.warn('⚠️ Failed to update pseudo in tournaments:', tournamentError.message);
        // Don't fail the entire operation if tournament update fails
      }
      
      setUserData(prev => ({ ...prev, pseudo: newPseudo }));
      setEditingPseudo(false);
      toast.success(t('profile.pseudoUpdated'));
    } catch (err) {
      toast.error(t('profile.failedUpdatePseudo') + err.message);
    } finally {
      setSaving(false);
    }
  };


  const handleSaveUsername = async () => {
    const user = getAuth().currentUser;
    if (!user) return;
    setSaving(true);
    try {
      // Use backup wrapper for username update
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, {
        username: newUsername
      }, 'profile_update', {
        field: 'username',
        previousValue: userData.username,
        newValue: newUsername
      });
      
      setUserData(prev => ({ ...prev, username: newUsername }));
      setEditingUsername(false);
      toast.success(t('profile.usernameUpdated'));
    } catch (err) {
      toast.error(t('profile.failedUpdateUsername') + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMasterDuelId = async () => {
    const user = getAuth().currentUser;
    if (!user) return;
    setSaving(true);
    try {
      // Use backup wrapper for Master Duel ID update
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, {
        masterDuelId
      }, 'profile_update', {
        field: 'masterDuelId',
        previousValue: userData.masterDuelId,
        newValue: masterDuelId
      });
      
      setEditingMasterDuelId(false);
      toast.success(t('profile.masterDuelIdUpdated'));
    } catch (err) {
      toast.error(t('profile.failedUpdateMasterDuelId') + err.message);
    } finally {
      setSaving(false);
    }
  };


  const [statsVisibility, setStatsVisibility] = useState({
    matches: true,
    wins: true,
    losses: true,
    champion: true,
  });

  const navigate = useNavigate();

  const touchStartY = useRef(0);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (deltaY > 100) setShowPreview(false);
  };

  const openPreview = (imageUrl) => {
    setPreviewImage(imageUrl);
    setShowPreview(true);
  };

  const closePreview = () => setShowPreview(false);

  const tournaments = pages[currentPageIndex] || [];

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return 'N/A';
    if (typeof createdAt === 'string') {
      const date = new Date(createdAt);
      return isNaN(date) ? 'Invalid Date' : date.toLocaleDateString();
    }
    if (createdAt.seconds) {
      return new Date(createdAt.seconds * 1000).toLocaleDateString();
    }
    return 'Invalid Date';
  };


  const fetchUserData = async (user) => {
    if (!user) return;
    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      setUserData(data);
      if (data.YP !== undefined) console.log("Loaded YP:", data.YP);
      if (data.statsVisibility) setStatsVisibility(data.statsVisibility);
      if (data.profilePicture) setImageUrl(data.profilePicture);
      if (data.stats) setStats(data.stats);
      if (data.masterDuelId) setMasterDuelId(data.masterDuelId);
    }
  };


  const fetchAchievements = async (user) => {
    if (!user) return;
    const colRef = collection(db, 'users', user.uid, 'achievements');
    const snapshot = await getDocs(colRef);
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setAchievements(docs);
  };


  const fetchTournaments = async (user) => {
    if (!user) return;
    const colRef = collection(db, 'users', user.uid, 'tournaments');
    const q = query(colRef, orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const ongoing = docs.filter(t => t.result === 'on going');
    setCurrentTournaments(ongoing.map(t => t.name));


    const pageSize = 5;
    const paginated = [];
    for (let i = 0; i < docs.length; i += pageSize) {
      paginated.push(docs.slice(i, i + pageSize));
    }
    setPages(paginated);
  };


  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      if (user) {
        fetchUserData(user);
        fetchAchievements(user);
        fetchTournaments(user);
      } else {
        // Optional: redirect to login if needed
        console.log("⚠️ No user signed in");
      }
    });

    return () => unsubscribe();
  }, []);


  useEffect(() => {
    setHasNext(currentPageIndex < pages.length - 1);
    setHasPrev(currentPageIndex > 0);
  }, [currentPageIndex, pages]);

  const handleNextPage = () => {
    if (hasNext) setCurrentPageIndex(prev => prev + 1);
  };

  const handlePrevPage = () => {
    if (hasPrev) setCurrentPageIndex(prev => prev - 1);
  };

  const handleCroppedProfileImage = async (croppedBlob) => {
    const user = getAuth().currentUser;
    if (!user) {
      console.error('❌ No authenticated user found');
      return;
    }
    
    console.log('📸 Starting profile picture upload for user:', user.uid);
    setUploading(true);
    const filename = `avatar.jpg`;
    const storageRef = ref(storage, `profilePictures/${user.uid}/${filename}`);
    setSaving(true);
    
    try {
      console.log('📤 Uploading to storage...');
      await uploadBytes(storageRef, croppedBlob);
      
      console.log('🔗 Getting download URL...');
      const downloadURL = await getDownloadURL(storageRef);
      
      console.log('💾 Updating user document with profilePicture:', downloadURL);
      // Use backup wrapper for profile picture update
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, { 
        profilePicture: downloadURL 
      }, 'profile_update', {
        field: 'profilePicture',
        previousValue: userData.profilePicture,
        newValue: downloadURL
      });
      
      setImageUrl(downloadURL);
      console.log('✅ Profile picture updated successfully');
      toast.success(t('profile.profilePictureUpdated'));
    } catch (err) {
      console.error('❌ Profile picture upload error:', err);
      toast.error(t('profile.failedUploadCroppedImage') + err.message);
    } finally {
      setShowCropModal(false);
      setUploading(false);
      setSaving(false);
    }
  };

  const handleCroppedBannerImage = async (croppedBlob) => {
    const user = getAuth().currentUser;
    if (!user) {
      console.error('❌ No authenticated user found');
      return;
    }
    
    console.log('🖼️ Starting banner upload for user:', user.uid);
    setUploading(true);
    const filename = `banner.jpg`;
    const storageRef = ref(storage, `bannerImages/${user.uid}/${filename}`);
    setSaving(true);
    
    try {
      console.log('📤 Uploading banner to storage...');
      await uploadBytes(storageRef, croppedBlob);
      
      console.log('🔗 Getting banner download URL...');
      const downloadURL = await getDownloadURL(storageRef);
      
      console.log('💾 Updating user document with bannerPicture:', downloadURL);
      // Use backup wrapper for banner picture update
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, { 
        bannerPicture: downloadURL 
      }, 'profile_update', {
        field: 'bannerPicture',
        previousValue: userData.bannerPicture,
        newValue: downloadURL
      });
      
      setUserData(prev => ({ ...prev, bannerPicture: downloadURL }));
      console.log('✅ Banner updated successfully');
      toast.success(t('profile.bannerUpdated'));
    } catch (err) {
      console.error('❌ Banner upload error:', err);
      toast.error(t('profile.failedUploadBanner') + err.message);
    } finally {
      setShowBannerCrop(false);
      setUploading(false);
      setSaving(false);
    }
  };

  const handleSaveVisibility = async () => {
    const user = getAuth().currentUser;
    if (!user) return;
    setSaving(true);
    try {
      // Stats visibility is just a UI setting, no backup needed
      await updateDoc(doc(db, 'users', user.uid), {
        statsVisibility
      });
      
      toast.success(t('profile.statsVisibilityUpdated'));
    } catch (err) {
      toast.error(t('profile.failedUpdateVisibility') + err.message);
    } finally {
      setSaving(false);
    }
  };

  const fetchGlobalQuests = async () => {
    const colRef = collection(db, 'quests');
    const snapshot = await getDocs(colRef);
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setGlobalQuests(docs);
  };
  const fetchUserQuestProgress = async () => {
    const user = getAuth().currentUser;
    if (!user) return;
    const colRef = collection(db, 'users', user.uid, 'questProgress');
    const snapshot = await getDocs(colRef);
    const progressData = {};
    snapshot.forEach(doc => {
      progressData[doc.id] = doc.data();
    });
    setUserQuestProgress(progressData);
  };



  const handleOpenQuestModal = () => {
    fetchGlobalQuests();
    fetchUserQuestProgress();
    setShowQuestModal(true);
  };



  if (!userData) return <div>{t('profile.loadingProfileText')}</div>;

  return (
    <div className="my-profile-container">
      {/* Banner & Avatar */}
      <div className="top-profile-image">
        <div className="my-profile-banner">
          <img src={userData.bannerPicture || '/default-banner.webp'} alt="Banner" className="my-banner-img" onClick={() => openPreview(userData.bannerPicture || '/default-banner.webp')} />
          <div className="banner-camera-overlay" onClick={() => document.getElementById('banner-pic-input').click()}>
            <img src={CameraIcon} alt="Edit Banner" className="camera-icon" />
          </div>
          <div className="my-profile-picture-wrapper">
            <img src={imageUrl || '/default-avatar.png'} alt="Profile" className={`my-profile-picture ${userData.division?.toLowerCase().replace(' ', '-') || ''}`} onClick={() => openPreview(imageUrl || '/default-avatar.png')} />
            <div className="camera-overlay" onClick={() => document.getElementById('profile-pic-input').click()}>
              <img src={CameraIcon} alt="Edit Profile" className="camera-icon" />
            </div>
          </div>
        </div>
        <input id="profile-pic-input" type="file" accept="image/*" onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              setRawImage(reader.result);
              setShowCropModal(true);
            };
            reader.readAsDataURL(file);
          }
        }} style={{ display: 'none' }} disabled={uploading} />
        <input id="banner-pic-input" type="file" accept="image/*" onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              setRawBanner(reader.result);
              setShowBannerCrop(true);
            };
            reader.readAsDataURL(file);
          }
        }} style={{ display: 'none' }} disabled={uploading} />
      </div>

      <div className="my-profile-info-box">
        {!editingPseudo ? (
          <div className="pseudo-edit-row">
            <h2
              className="pseudo-title clickable-pseudo"
              onClick={() => {
                setNewPseudo(userData.pseudo || '');
                setEditingPseudo(true);
              }}
            >
              {userData.pseudo || t('profile.noPseudo')} <span style={{ fontSize: '0.6em', opacity: 1, marginLeft: '4px' }}>✏️</span>
            </h2>
          </div>
        ) : (
          <div className="master-duel-id-edit">
            <input
              type="text"
              value={newPseudo}
              onChange={(e) => setNewPseudo(e.target.value)}
              placeholder={t('profile.enterPseudo')}
              autoFocus
              disabled={saving}
            />
            <div className="button-group">
              <button 
                onClick={handleSavePseudo} 
                disabled={saving || !newPseudo.trim()}
                className={saving ? 'saving' : ''}
              >
                {saving ? t('profile.saving', 'Saving...') : t('profile.save')}
              </button>
              <button 
                onClick={() => setEditingPseudo(false)} 
                disabled={saving}
                className={saving ? 'disabled' : ''}
              >
                {t('profile.cancel')}
              </button>
            </div>
          </div>
        )}


        <p><strong>{t('menu.dp')}:</strong> {userData.dp}</p>
        <p><strong>{t('profile.yp')}:</strong> {userData.YP ?? (stats?.wins ? stats.wins * 2 : 0)}</p>

        {!editingMasterDuelId ? (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
            <strong>{t('profile.masterDuelId')}:</strong>
            <span
              className="clickable-master-duel-id"
              onClick={() => setEditingMasterDuelId(true)}
              style={{
                cursor: 'pointer',
                wordBreak: 'break-all',
                maxWidth: '100%',
                display: 'inline-block'
              }}
            >
              {masterDuelId || t('profile.notSet')}
            </span>
          </div>
        ) : (
          <div className="master-duel-id-edit">
            <input
              type="text"
              value={masterDuelId}
              onChange={(e) => setMasterDuelId(e.target.value)}
              placeholder={t('profile.enterMasterDuelId')}
              autoFocus
              disabled={saving}
            />
            <div className="button-group">
              <button 
                onClick={handleSaveMasterDuelId} 
                disabled={saving}
                className={saving ? 'saving' : ''}
              >
                {saving ? t('profile.saving', 'Saving...') : t('profile.save')}
              </button>
              <button 
                onClick={() => setEditingMasterDuelId(false)} 
                disabled={saving}
                className={saving ? 'disabled' : ''}
              >
                {t('profile.cancel')}
              </button>
            </div>
          </div>
        )}

        <p><strong>{t('profile.joined')}:</strong> {formatCreatedAt(userData.createdAt)}</p>
        {uploading && <p className="uploading-text">{t('profile.uploading')}</p>}
      </div>

      {/* Stats */}
      {stats && (
        <div className="my-profile-stats-box">
          <h3 className="section-title">{t('profile.stats')}</h3>
          {currentTournaments.length > 0 && (
            <p className="current-tournament-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H5V21H19V9Z" fill="currentColor"/>
              </svg>
              <strong>{t('profile.currentTournament')}:</strong> {currentTournaments.join(' / ')}
            </p>
          )}

          <div className="stats-list">
            {['matches', 'wins', 'losses', 'champion'].map(key => (
              <div className="stat-row" key={key}>
                <span className="stat-label">
                  {t(`profile.${key}`)}: {stats[key]}
                </span>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    checked={statsVisibility[key]}
                    onChange={(e) =>
                      setStatsVisibility(prev => ({
                        ...prev,
                        [key]: e.target.checked
                      }))
                    }
                  />
                  {t('profile.show')}
                </label>
              </div>
            ))}
          </div>
          <button className="showVisibilityButton" onClick={handleSaveVisibility} disabled={saving}>{t('profile.update')}</button>
        </div>
      )}

      {/* Rewards Section */}
      <ProfileRewards userData={userData} />

      {/* Tournaments */}
      <div className="profile-section">
        <h3 className="section-title">{t('profile.tournaments')}</h3>
        {tournaments.length > 0 ? tournaments.map(tournament => (
          <div key={tournament.id} className="achievement-row">
            <img src={tournament.tournamentIcon || '/default-tournament.png'} alt="Tournament Icon" className="achievement-icon" />
            <div className="achievement-info">
              <div className="achievement-name">{tournament.name || t('profile.unnamedTournament')}</div>
              <div className="achievement-description">
                {t('profile.ranking')}: {tournament.result || t('profile.participation')}<br />
                {t('profile.date')}: {tournament.date?.seconds ? new Date(tournament.date.seconds * 1000).toLocaleDateString() : 'Unknown'}
              </div>
            </div>
          </div>
        )) : <p>{t('profile.noTournaments')}</p>}

        <div className="pagination-container">
          <div className="pagination-buttons">
            <button disabled={!hasPrev} onClick={handlePrevPage}>{t('profile.prev')}</button>
            {pages.map((_, i) => (
              <span key={i} className={`page-number ${i === currentPageIndex ? 'active' : ''}`} onClick={() => setCurrentPageIndex(i)}>{i + 1}</span>
            ))}
            <button disabled={!hasNext} onClick={handleNextPage}>{t('profile.next')}</button>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="profile-section">
        <h3 className="section-title">{t('profile.achievements')}</h3>
        {/* TODO: Quest button needs improvement - temporarily commented out */}
        {/* <button className="quest-button" onClick={handleOpenQuestModal}>
          {t('profile.questButton') || 'Quest!'}
        </button> */}

        {achievements.length > 0 ? (
          <div className="achievements-list">
            {achievements.map(a => (
              <div key={a.id} className="achievement-row">
                <img
                  src={a.icon}
                  alt={a.name}
                  className={`achievement-icon ${a.special ? 'special-glow' : ''}`}
                />

                <div className="achievement-info">
                  <div className="achievement-name">{a.name}</div>
                  <div className="achievement-description">{a.description}</div>
                </div>
                <div className="achievement-date">{new Date(a.dateEarned.seconds * 1000).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        ) : <p>{t('profile.noAchievements')}</p>}
      </div>

      <div className="my-profile-actions">
        <button className="back-button" onClick={() => {
          playSound('return');
          navigate('/menu');
        }}>{t('profile.back')}</button>
      </div>

      {/* Modals */}
      {showCropModal && (
        <CropModal
          imageSrc={rawImage}
          onCancel={() => setShowCropModal(false)}
          onCropComplete={handleCroppedProfileImage}
          aspect={1}
          cropShape="round"
          disabled={uploading}
        />
      )}
      {showBannerCrop && (
        <CropModal
          imageSrc={rawBanner}
          onCancel={() => setShowBannerCrop(false)}
          onCropComplete={handleCroppedBannerImage}
          aspect={16 / 5}
          cropShape="rect"
          disabled={uploading}
        />
      )}

      {previewImage && (
        <div className="image-preview-overlay" onClick={() => setPreviewImage(null)}>
          <img src={previewImage} alt="Preview" className="image-preview-full" />
        </div>
      )}

      {showPreview && (
        <div className="preview-overlay" onClick={closePreview} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
          <img src={previewImage} alt="Preview" className="preview-image" />
        </div>
      )}

      {popupMessage && (
        <PopupModal message={popupMessage} onClose={() => setPopupMessage('')} />
      )}

      {showQuestModal && (
        <div className="quest-modal-overlay" onClick={() => setShowQuestModal(false)}>
          <div className="quest-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('profile.quests') || 'Quests'}</h2>
            {globalQuests.length > 0 ? (
              <ul className="quest-list">
                {globalQuests.map(q => {
                  const progressEntry = userQuestProgress[q.id] || { progress: 0, completed: false };
                  return (
                    <li key={q.id} className="quest-item">
                      {q.imageUrl && (
                        <img
                          src={q.imageUrl}
                          alt={q.title}
                          className="quest-icon"
                        />
                      )}
                      <div className="quest-info">
                        <h3>{q.title}</h3>
                        <p>{q.description}</p>
                        <p>
                          <strong>{t('profile.progress')}:</strong> {progressEntry.progress} / {q.goal}
                        </p>
                        {progressEntry.completed && (
                          <span className="quest-completed">{t('profile.completed')}</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p>{t('profile.noQuests') || 'No quests available.'}</p>
            )}
            <button className="close-quest-button" onClick={() => setShowQuestModal(false)}>
              {t('profile.close') || 'Close'}
            </button>
          </div>
        </div>
      )}




    </div>
  );
};

export default MyProfilePage;
